/**
 * Which screen a pushed page was opened from, and therefore which tab it is in.
 *
 * Two questions on every pushed screen have the same answer, and until now
 * neither was being asked. The back chip hard-coded the name of one screen
 * ("← TRIP" on the journey, when there was a Trip tab) though the board could
 * open the same page. And the
 * tab bar highlighted nothing at all: `ion-tab-bar` picks its selected button
 * by matching the current path against a button's own `href`, and
 * `/tabs/journey/2` matches none of the four. A journey opened from the Trains
 * tab must say TRAINS on the chip AND light TRAINS in the bar, and the only way
 * those two can never disagree is for both to read the same record.
 *
 * WHY NOT THE ROUTER'S OWN HISTORY. This app runs `createMemoryHistory`, whose
 * `state` is an empty object — `history.state.back` is never set, and
 * `useIonRouter().canGoBack()` answers `undefined`. `RuleEditPage.leave()`
 * carries the scar of finding that out. So there is nothing underneath to read,
 * and the truthful moment to record where somebody came from is the moment they
 * leave it: the push itself.
 *
 * RECORDED AT THE PUSH, AND NOWHERE ELSE. A `router.afterEach` hook was written
 * and taken out again: it cannot tell a push from a pop, and worse, switching
 * tabs away and back makes Ionic re-push the pushed page from its own
 * `currentHref` — which would rewrite the journey's origin as "the tab you just
 * left" every time somebody glanced at another tab. Only an explicit
 * `openScreen` writes, so a restore, a back and a reload all leave the record
 * exactly as the person's own navigation left it.
 *
 * KEYED BY THE DESTINATION PATH, so two pushed screens on the stack each keep
 * their own answer and a screen re-opened from somewhere else overwrites its
 * own record and nobody else's. The map is capped: a session opens a lot of
 * train cards and none of the old ones is worth a byte.
 */

import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { PRODUCT_ROUTES } from './product-routes.js';
import { shownModes } from './app-state.js';
import { departuresLabel } from './tab-label.js';

/** Plenty for a stack nobody can push more than a handful deep. */
const KEEP = 30;

/**
 * Destination path → `{ tab, backName }` of the screen underneath it.
 *
 * A ref because the tab bar renders from it: the highlight has to move in the
 * same tick the page does, or the bar spends a frame lit for the screen you
 * have just left.
 */
const origins = ref(new Map());

/**
 * The screens by id, built ON FIRST USE and not at import.
 *
 * `product-routes.js` imports `HomePage.vue` (it is the first paint and is not
 * lazy), the page imports this module, and this module imports the routes —
 * a cycle, and a cycle evaluated at module scope reads the half-built end of it
 * as `undefined`. Nothing asks a question of a route until somebody navigates,
 * by which time every module is whole.
 */
let byId = null;
function routeById(routeId) {
  if (byId === null) byId = new Map(PRODUCT_ROUTES.map((r) => [r.id, r]));
  return byId.get(String(routeId)) ?? null;
}

/** The tab a screen belongs to when nothing was recorded: its own declaration. */
export function tabOf(routeId) {
  return routeById(routeId)?.tab ?? null;
}

/** What a back chip on another screen calls this one. */
export function nameOf(routeId) {
  const name = routeById(routeId)?.backName ?? 'BACK';
  // The first tab is BOARD on a phone with another mode on, and the chip says
  // what the bar says. See src/tab-label.js.
  return name === 'TRAINS' && shownModes.value.length ? departuresLabel(shownModes.value.length).toUpperCase() : name;
}

/**
 * Where the page at `path` was opened from: `{ tab, backName }`, or null.
 *
 * Null is a page nobody pushed — a reload, a test mounting the component on
 * its own — and every caller answers it with its own declared default rather
 * than naming a screen that may not be underneath.
 */
export function originOf(path) {
  return origins.value.get(String(path)) ?? null;
}

/**
 * Push `path`, remembering the screen being left.
 *
 * The screen being left is `router.currentRoute`, and its tab is whichever tab
 * IT is in: a tab route is its own tab, and a pushed screen inherits the tab of
 * the screen it was opened from — so a train card opened from a journey opened
 * from the board is still in the Trains tab three screens down.
 */
export function openScreen(router, path) {
  const from = router.currentRoute.value;
  const id = String(from?.name ?? '');
  const inherited = originOf(from?.fullPath ?? from?.path ?? '');
  // A TAB ROUTE DECLARES ITSELF: its `tab` is its own id, and that is how one
  // is told from a pushed screen here without importing the bar's own list.
  // A pushed screen's RECORD beats its declaration — the declaration is only
  // the fallback for a screen nobody pushed — because a train card opened from
  // a journey opened from the board is in the Trains tab, whatever the train
  // card's usual home is.
  const own = tabOf(id);
  const record = {
    tab: own === id ? id : inherited?.tab ?? own,
    backName: nameOf(id),
  };

  const next = new Map(origins.value);
  next.delete(path);
  next.set(path, record);
  // Oldest first in insertion order, so the excess comes off the front.
  while (next.size > KEEP) next.delete(next.keys().next().value);
  origins.value = next;

  return router.push(path);
}

/**
 * What this screen's back chip should say, as a live label.
 *
 * `routeId` is the screen's own id, and its answer when nothing was recorded is
 * the name of the tab it declares itself in — the Trains tab for a journey or
 * a train card, the Lines tab for a line. That is the only screen that can be underneath a
 * page nobody pushed, because a tab root is where a stack starts.
 */
export function useBackName(routeId) {
  const route = useRoute();
  return computed(() => originOf(route.fullPath)?.backName ?? nameOf(tabOf(routeId)));
}

/**
 * Replace the current screen with `path`, keeping what is underneath.
 *
 * A notice that opens another notice in its place has not moved anybody a
 * screen deeper: the page under both is the ledger row, or the journey, or
 * whatever pushed the first one. So the record is carried across rather than
 * rewritten, and the chip goes on naming the screen that is actually there.
 */
export function replaceScreen(router, path) {
  const from = router.currentRoute.value;
  const carried = originOf(from?.fullPath ?? from?.path ?? '');
  if (carried) {
    const next = new Map(origins.value);
    next.delete(path);
    next.set(path, carried);
    while (next.size > KEEP) next.delete(next.keys().next().value);
    origins.value = next;
  }
  return router.replace(path);
}

/** Tests only. */
export function clearOrigins() {
  origins.value = new Map();
}
